<script setup lang="ts">
import SectionDetailsItem from '../../components/SectionDetailsItem/SectionDetailsItem.vue';
import ListWrapperCard from 'shared/components/ListWrapperCard/ListWrapperCard.vue';
import { useRouter } from 'vue-router';
import { toFilterableItem, useUserSectionData } from '../useUserSectionData';
import type { Filter } from '../useUserSectionData';
import { listItemStatus } from 'shared/composables/useListFilters/useListFilters';
import { useQuasar } from 'quasar';
import { t } from 'src/boot/i18n';
import { computed, inject, ref, watch } from 'vue';
import type { ComputedRef } from 'vue';
import type { UserRole } from 'src/types/User';
import ContentEmptyState from 'src/shared/components/ContentEmptyState/ContentEmptyState.vue';
import BListFilterBar from 'src/shared/components/base/BListFilterBar/BListFilterBar.vue';
import { useListFilters } from 'shared/composables/useListFilters/useListFilters';
import { hasSectionStarted } from '../../utilities/sectionProgressState';
import { shouldStopSectionListInfiniteScroll } from './utilities/sectionListInfiniteScroll';
import {
  resolveSectionDisplayScore,
  resolveSectionDisplayTime,
} from '../../utilities/sectionDisplayMetrics';
import type { QInfiniteScroll } from 'quasar';

interface Props {
  filter?: Filter;
}

const props = defineProps<Props>();

const {
  criteria,
  activeFilterCount,
  hideCompleted,
  sortBy,
  search,
  selectedStatuses,
  toggleStatus,
  clearAll,
} = useListFilters();

const {
  userSections,
  baseUserSections,
  getUserSections,
  sectionTotal,
  isLastSection,
  isLastFilteredSection,
  isLoading,
} = useUserSectionData({ filter: props?.filter, criteria });
const router = useRouter();
const $q = useQuasar();
const currentUserRole =
  inject<ComputedRef<UserRole | undefined>>('currentUserRole');
const isSchoolStudent = computed(() => currentUserRole?.value === 'student');

const criteriaActive = computed(
  () => activeFilterCount.value > 0 || hideCompleted.value,
);

const infiniteScrollRef = ref<QInfiniteScroll | null>(null);

// Criteria changes repaginate server-side: the mirrored repo query already
// updated the visible list, this refetch fills page 1 of the new ordering and
// re-arms the infinite scroll for the rest. Page-level filter tabs
// (is_assigned / started_only) fetched everything up front, so their mirror
// is already complete — no refetch needed.
watch(criteria, () => {
  if (props.filter?.key) return;
  void getUserSections().then(() => {
    infiniteScrollRef.value?.reset();
    infiniteScrollRef.value?.resume();
  });
});

// Empty list (once loading settles): explain why it's empty and what to do next.
const showEmptyState = computed(
  () => !isLoading.value && !userSections.value.length,
);

// Criteria emptied a non-empty list — say so instead of "you have no sections".
const showFilteredEmptyState = computed(
  () =>
    showEmptyState.value &&
    criteriaActive.value &&
    baseUserSections.value.length > 0,
);

function isSectionPaused(userSection: {
  is_assigned?: boolean;
  start_date?: string | null;
  completion_date?: string | null;
  time_accumulator?: number | null;
  score?: number | null;
  progress?: { steps?: unknown[] } | null;
}) {
  return userSection?.is_assigned === false && hasSectionStarted(userSection);
}

function isSectionPausedForCurrentRole(userSection: {
  is_assigned?: boolean;
  start_date?: string | null;
  completion_date?: string | null;
  time_accumulator?: number | null;
  score?: number | null;
  progress?: { steps?: unknown[] } | null;
}) {
  return isSchoolStudent.value && isSectionPaused(userSection);
}

const handleSelected = (userSectionDocId: string) => {
  const selectedSection = userSections.value.find(
    (userSection) => userSection.documentId === userSectionDocId,
  );
  if (selectedSection && isSectionPausedForCurrentRole(selectedSection)) {
    $q.notify({
      type: 'warning',
      message: t('SCHOOL_UNASSIGNED_PROGRESS_PAUSED_MESSAGE'),
    });
    return;
  }

  void router.push({ name: 'section.steps', params: { userSectionDocId } });
};

function onLoad(index: number, done: (stop: boolean) => void): void {
  void index;
  void getUserSections();
  done(
    shouldStopSectionListInfiniteScroll(
      props.filter?.key,
      isLastSection.value,
      {
        active: criteriaActive.value,
        isLastFilteredSection: isLastFilteredSection.value,
      },
    ),
  );
}
</script>

<template>
  <ListWrapperCard
    flat
    class="fit bg-transparent q-pa-none q-mt-sm"
  >
    <BListFilterBar
      v-model:hide-completed="hideCompleted"
      v-model:sort-by="sortBy"
      v-model:search="search"
      :selected-statuses="selectedStatuses"
      :active-filter-count="activeFilterCount"
      :shown-count="userSections.length"
      :total-count="
        props.filter?.key
          ? baseUserSections.length
          : (sectionTotal ?? baseUserSections.length)
      "
      @toggle:status="toggleStatus"
      @clear="clearAll"
    />
    <q-banner
      v-if="showFilteredEmptyState"
      class="text-center q-mt-lg bg-transparent"
    >
      <p class="q-mb-sm">{{ $t('NO_TASKS_MATCH_FILTERS') }}</p>
      <q-btn
        flat
        no-caps
        color="accent"
        :label="$t('CLEAR_ALL')"
        @click="clearAll"
      />
    </q-banner>
    <ContentEmptyState
      v-else-if="showEmptyState"
      content-type="section"
    />
    <q-infinite-scroll
      v-else
      ref="infiniteScrollRef"
      :offset="150"
      @load="onLoad"
    >
      <q-list
        v-if="userSections"
        class="q-gutter-y-xs"
      >
        <template
          v-for="userSection in userSections"
          :key="userSection.id"
        >
          <SectionDetailsItem
            :section-name="userSection?.section?.section_name ?? ''"
            :subtitle="userSection?.section?.subtitle"
            :paused-message="
              isSectionPausedForCurrentRole(userSection)
                ? $t('SCHOOL_UNASSIGNED_PROGRESS_PAUSED_MESSAGE')
                : null
            "
            :start-date="userSection?.start_date"
            :score="resolveSectionDisplayScore(userSection)"
            :document-id="userSection?.documentId ?? ''"
            :time-accumulator="resolveSectionDisplayTime(userSection)"
            :status="listItemStatus(toFilterableItem(userSection))"
            :disable="isSectionPausedForCurrentRole(userSection)"
            @section:selected="handleSelected(userSection?.documentId ?? '')"
          />
        </template>
      </q-list>
      <template #loading>
        <div class="row justify-center q-pa-md">
          <q-spinner
            size="40px"
            color="primary"
          />
        </div>
      </template>
    </q-infinite-scroll>
  </ListWrapperCard>
</template>
